export type ViewportWidthBucket = '<1024' | '1024-1279' | '1280-1439' | '1440-1919' | '>=1920';

export type ViewportHeightBucket = '<600' | '600-699' | '700-799' | '800-999' | '>=1000';

export interface ScreenTraits {
  viewportWidth: number;
  viewportHeight: number;
  viewportWidthBucket: ViewportWidthBucket;
  viewportHeightBucket: ViewportHeightBucket;
  /** Screen in CSS px — a 1920 monitor at Windows 150% reports 1280. Page zoom does not move it. */
  screenWidth: number;
  screenHeight: number;
  /** OS scaling x browser zoom on Chrome/Firefox. Safari keeps page zoom out of it. */
  devicePixelRatio: number;
  /** null when unmeasurable, never 1 — a default would read as "not zoomed" and undercount. */
  browserZoom: number | null;
}

const CHROMIUM_ZOOM_STEPS = [0.25, 0.33, 0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3, 4, 5];

const FIREFOX_ZOOM_STEPS = [0.3, 0.5, 0.67, 0.8, 0.9, 1, 1.1, 1.2, 1.33, 1.5, 1.7, 2, 2.4, 3, 5];

/**
 * Picked per engine rather than unioned: together the two ladders put steps 0.05 apart (1.2/1.25,
 * 1.7/1.75), and window-border skew is multiplicative — a Firefox 170% on a bordered window reads
 * 1.727 and lands on 1.75, splitting one zoom level across two reported values.
 */
const zoomStepsForEngine = () => (navigator.userAgent.includes('Firefox') ? FIREFOX_ZOOM_STEPS : CHROMIUM_ZOOM_STEPS);

/** Measured raw error is 0.001. An order of magnitude past that is a skewed measurement, not a step. */
const ZOOM_SNAP_TOLERANCE = 0.03;

const widthBucketOf = (width: number): ViewportWidthBucket => {
  if (width < 1024) return '<1024';
  if (width < 1280) return '1024-1279';
  if (width < 1440) return '1280-1439';
  if (width < 1920) return '1440-1919';
  return '>=1920';
};

const heightBucketOf = (height: number): ViewportHeightBucket => {
  if (height < 600) return '<600';
  if (height < 700) return '600-699';
  if (height < 800) return '700-799';
  if (height < 1000) return '800-999';
  return '>=1000';
};

const round2 = (value: number) => Math.round(value * 100) / 100;

/**
 * Page zoom isolated from OS scaling and panel density: `outerWidth` is the browser frame and
 * ignores zoom, `innerWidth` is the content area and tracks it. Measured exact across Chrome's
 * 100-200% steps (max raw error 0.001), and unaffected by dpr or scrollbar width.
 *
 * `innerWidth` is the right denominator — `clientWidth` excludes the scrollbar and skews ~0.8%.
 *
 * Returns null when the ratio sits too far from any step of the engine's ladder.
 */
const measureBrowserZoom = (): number | null => {
  const { innerWidth, outerWidth } = window;

  // outerWidth is 0 inside an iframe, where the frame is not the browser window.
  if (!(innerWidth && outerWidth)) return null;

  const raw = outerWidth / innerWidth;
  const steps = zoomStepsForEngine();
  const closest = steps.reduce((best, step) => (Math.abs(step - raw) < Math.abs(best - raw) ? step : best), steps[0]);

  // Same reasoning as the null above: a wrong value reads as real, a null reads as unmeasured.
  // Side-docked DevTools shrinks innerWidth without touching outerWidth and would otherwise report
  // a plausible-looking zoom nobody set.
  return Math.abs(closest - raw) <= ZOOM_SNAP_TOLERANCE ? closest : null;
};

/** Uncached read. `captureScreenTraits` is what the destinations use; this is the primitive under it. */
export const readScreenTraits = (): ScreenTraits => ({
  viewportWidth: window.innerWidth,
  viewportHeight: window.innerHeight,
  viewportWidthBucket: widthBucketOf(window.innerWidth),
  viewportHeightBucket: heightBucketOf(window.innerHeight),
  screenWidth: window.screen.width,
  screenHeight: window.screen.height,
  devicePixelRatio: round2(window.devicePixelRatio || 1),
  browserZoom: measureBrowserZoom(),
});

let captured: ScreenTraits | null = null;

/**
 * Frozen first-render snapshot, shared by all four destinations so Segment, PostHog, Datadog RUM and
 * Clarity cannot disagree about the same session. Call once at bootstrap to fix the capture point.
 */
export const captureScreenTraits = (): ScreenTraits => {
  captured ??= readScreenTraits();
  return captured;
};
